'use client';

import { StoryblokReactComponent } from './componentsMap';
import { storyblokEditable } from '@storyblok/js';
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import clsx from 'clsx';
import Link from 'next/link';
import { StoryblokStory } from 'storyblok-generate-ts';
import { Step123ItemStoryblok, Step123Storyblok, StoryblokComponent } from '@/storyblok/types';
import { getImageDimensions } from '@/storyblok/utils/asset';
import { resolveWebLink } from '@/storyblok/utils/link';
import { monaFont } from '@/styles/fonts';

export const StoryblokStep123: StoryblokReactComponent<Step123Storyblok> = ({ component, story }) => {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.matchMedia('(max-width: 1080px)').matches : true);
  const [selectedStepUid, setSelectedStepUid] = useState(component.steps?.at(0)?._uid);
  const stepRefs = useRef<{ [key: string]: { el: HTMLDivElement | null; openHeight: number } }>({});
  const [stepHeight, setStepHeight] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    window.matchMedia('(max-width: 1080px)').addEventListener('change', e => setIsMobile(e.matches));
    if (!isMobile) return;
    const { key, height } = Object.entries(stepRefs.current).reduce(
      (acc, [key, { el }]) => ((el?.scrollHeight ?? 0) > acc.height ? { key, height: el?.scrollHeight ?? 0 } : acc),
      { key: '', height: 0 },
    );
    stepRefs.current[key] = { ...stepRefs.current[key], openHeight: height };
    setStepHeight(height);
  }, [isMobile, selectedStepUid]);

  return (
    <>
      <section className="mx-auto hidden max-w-section flex-col lg:mx-20 lg:flex xl:mx-auto" {...storyblokEditable(component)}>
        {component.title && <h2 className={clsx(monaFont.className, 'text-center text-5xl font-bold leading-tight')}>{component.title}</h2>}
        {component.subtitle && <p className="mt-6 text-center text-xl font-[400] text-neutral-lighter">{component.subtitle}</p>}
        <div
          className={clsx(
            'flex flex-col items-center rounded-xl border py-12 shadow-2',
            (component.title || component.subtitle) && 'mt-10',
          )}
        >
          <nav className="w-full px-6">
            <ul className="grid w-full" style={{ gridTemplateColumns: `repeat(${component.steps?.length ?? 0}, 1fr)` }}>
              {(component.steps ?? []).map(step => (
                <li key={step._uid} className="shrink-0 flex-grow">
                  <button
                    className={clsx(
                      'flex w-full flex-row items-center justify-center gap-2  border-b-2 px-2 py-4 text-left text-xl transition-colors',
                      selectedStepUid === step._uid ? 'border-brandBlue-400 bg-brandBlue-50' : 'border-neutral-100 bg-white',
                    )}
                    onClick={() => setSelectedStepUid(step._uid)}
                  >
                    {step.icon?.filename && (
                      <Image
                        className={clsx(selectedStepUid !== step._uid && 'grayscale', 'size-5')}
                        src={step.icon.filename}
                        alt={step.icon.alt ?? ''}
                        {...getImageDimensions(step.icon)}
                      />
                    )}
                    {step.title && <span>{step.title}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
          {(component.steps ?? []).map(step => (
            <div key={step._uid} className={clsx('hidden', selectedStepUid !== step._uid ? 'lg:hidden' : 'w-full flex-row pt-16 lg:flex')}>
              <div className="flex max-w-96 flex-col justify-center gap-6 pl-14">
                {step.preTitle && <span className="font-semibold">{step.preTitle}</span>}
                {(step.tabTitle || step.title) && (
                  <h2 className={clsx(monaFont.className, 'text-4xl font-bold leading-tight')}>{step.title ?? step.tabTitle}</h2>
                )}
                {step.description && <p>{step.description}</p>}
                {step.cta?.[0] && (
                  <div className="mt-6 flex flex-row">
                    <Link
                      className="rounded-lg bg-primary px-16 py-4 text-center font-medium text-white transition-colors hover:bg-primary-hover"
                      href={resolveWebLink(step.cta[0].link, { story })}
                    >
                      {step.cta[0].label}
                    </Link>
                  </div>
                )}
              </div>
              <div className="flex flex-grow flex-row justify-end pr-14 lg:h-[320px] xl:h-[420px]">
                <div className="max-h-full rounded-xl bg-neutral-inactive">
                  {step.image && (
                    <Image
                      className="h-full w-auto rounded-xl"
                      src={step.image?.filename}
                      alt={step.image?.alt ?? ''}
                      {...getImageDimensions(step.image)}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      <section ref={containerRef} className="mx-4 md:mx-20 lg:hidden">
        {component.title && <h2 className={clsx(monaFont.className, 'text-4xl font-bold leading-tight')}>{component.title}</h2>}
        {component.subtitle && <p className="mt-6 text-xl font-[400] text-neutral-lighter">{component.subtitle}</p>}
        <ul className={clsx((component.title || component.subtitle) && 'mt-12')}>
          {component.steps?.map(step => (
            <li key={step._uid} className="mb-4">
              <button className="flex w-full flex-row items-center p-4 text-xl" onClick={() => setSelectedStepUid(step._uid)}>
                {step.icon?.filename && (
                  <Image className="size-5" src={step.icon.filename} alt={step.icon.alt ?? ''} {...getImageDimensions(step.icon)} />
                )}
                {step.title && <span className="ml-4">{step.title}</span>}
                <div className="flex flex-grow flex-row justify-end">
                  {step._uid === selectedStepUid ? (
                    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M5.75 12C5.75 11.5858 6.08579 11.25 6.5 11.25L18.5 11.25C18.9142 11.25 19.25 11.5858 19.25 12C19.25 12.4142 18.9142 12.75 18.5 12.75L6.5 12.75C6.08579 12.75 5.75 12.4142 5.75 12Z"
                        fill="#5182EF"
                      />
                    </svg>
                  ) : (
                    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M12.5 3.75C12.9142 3.75 13.25 4.08579 13.25 4.5V11.25H20C20.4142 11.25 20.75 11.5858 20.75 12C20.75 12.4142 20.4142 12.75 20 12.75H13.25V19.5C13.25 19.9142 12.9142 20.25 12.5 20.25C12.0858 20.25 11.75 19.9142 11.75 19.5V12.75H5C4.58579 12.75 4.25 12.4142 4.25 12C4.25 11.5858 4.58579 11.25 5 11.25H11.75V4.5C11.75 4.08579 12.0858 3.75 12.5 3.75Z"
                        fill="#5182EF"
                      />
                    </svg>
                  )}
                </div>
              </button>
              <MobileStepContent
                selected={selectedStepUid === step._uid}
                story={story}
                step={step}
                key={step._uid}
                height={stepRefs.current[step._uid]?.openHeight || stepHeight}
                ref={el => {
                  stepRefs.current[step._uid] = { el, openHeight: el?.scrollHeight ?? 0 };
                }}
              />
              <div className="border-b-2 border-neutral-100" />
            </li>
          ))}
        </ul>
      </section>
    </>
  );
};

const MobileStepContent = React.forwardRef<
  HTMLDivElement,
  { selected: boolean; step: Step123ItemStoryblok; height: number; story: StoryblokStory<StoryblokComponent> }
>(({ step, selected, height, story }, ref) => (
  <div
    ref={ref}
    className={clsx('flex flex-col gap-5 overflow-hidden transition-all duration-500 ')}
    style={{ maxHeight: selected ? height : '0', paddingBottom: !selected ? 0 : undefined }}
  >
    {!step.textBeforeImageOnMobile && step.image && (
      <Image className="w-full rounded-xl" src={step.image.filename} alt={step.image.alt ?? ''} {...getImageDimensions(step.image)} />
    )}
    <div className="py-2">
      {step.preTitle && <span className="mb-1 inline-block text-xs font-semibold">{step.preTitle}</span>}
      {(step.tabTitle || step.title) && (
        <h2 className={clsx(monaFont.className, 'mb-3 text-2xl font-bold leading-tight')}>{step.title ?? step.tabTitle}</h2>
      )}
      {step.description && <p className="text-neutral-lighter">{step.description}</p>}
      {step.cta?.[0] && (
        <div className="mt-6 flex flex-row">
          <Link
            className="rounded-lg bg-primary px-16 py-4 text-center font-medium text-white transition-colors hover:bg-primary-hover"
            href={resolveWebLink(step.cta[0].link, { story })}
          >
            {step.cta[0].label}
          </Link>
        </div>
      )}
    </div>
    {step.textBeforeImageOnMobile && step.image && (
      <Image className="w-full rounded-xl" src={step.image.filename} alt={step.image.alt ?? ''} {...getImageDimensions(step.image)} />
    )}
    <div className="h-6" />
  </div>
));
MobileStepContent.displayName = 'MobileStepContent';
